<template>
  <div
    class="cn-treeview-wrapper space-y-6"
    :class="{
      'cn-treeview-wrapper--disabled': disabled,
    }"
  >
    <Tree
      v-model:expanded-keys="expandedKeys"
      class="cn-treeview"
      selection-mode="checkbox"
      :value="nodes"
      :selection-keys="selectionKeys"
      @update:selection-keys="handleSelection"
    >
      <!-- PrimeVue's own toggle icon is a thin chevron; vue-treeselect drew a
           filled caret, pointing down while the branch is open -->
      <template #nodetoggleicon="{ expanded }">
        <svg
          class="cn-treeview__toggle-icon"
          viewBox="0 0 10 10"
          aria-hidden="true"
        >
          <path
            :d="expanded ? 'M0 3h10L5 8z' : 'M3 0v10l5-5z'"
            fill="currentColor"
          />
        </svg>
      </template>
    </Tree>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Tree from 'primevue/tree';
import type { TreeSelectionKeys } from 'primevue/tree';
import type { TreeNode } from 'primevue/treenode';
import {
  childrenOf,
  flatten,
  leavesOf,
  type TreeviewId,
  type TreeviewSource,
} from '@/components/ui/treeview';

interface Props {
  items?: TreeviewSource[];
  modelValue: TreeviewId[];
  disabled: boolean;
  disableBranchNodes?: boolean;
}

/** Selection state of a subtree, in PrimeVue's tri-state terms. */
type NodeState = 'checked' | 'partial' | 'none';

const props = withDefaults(defineProps<Props>(), { items: () => [] });
const emit = defineEmits<{
  (e: 'update:modelValue', value: TreeviewId[]): void;
}>();

// PrimeVue keys nodes by string; the model keeps the ids the API handed over
const keyOf = (source: TreeviewSource) => String(source.id);
// A branch is only pickable when branch nodes are allowed — in feature
// aggregations the branch is a group heading, not a value
const isSelectable = (source: TreeviewSource) =>
  !(childrenOf(source).length && props.disableBranchNodes);

const byId = computed(
  () => new Map(flatten(props.items).map((node) => [node.id, node])),
);

/**
 * The model holds branch ids whenever a whole branch is selected (what
 * vue-treeselect called BRANCH_PRIORITY), so working state is kept as leaves.
 */
const selectedLeaves = computed(() => {
  const leaves = props.modelValue.flatMap((id) => {
    const node = byId.value.get(id);
    return node ? leavesOf(node) : [];
  });
  return new Set(leaves);
});

/**
 * Ids the tree knows nothing about — a deep link filtering on features that
 * have not arrived yet. They are passed through untouched instead of being
 * dropped, which would silently widen the user's filter.
 */
const unknownIds = computed(() =>
  props.modelValue.filter((id) => !byId.value.has(id)),
);

const toTreeNode = (source: TreeviewSource): TreeNode => {
  const children = childrenOf(source);

  return {
    key: keyOf(source),
    label: source.name,
    selectable: isSelectable(source),
    ...(children.length ? { children: children.map(toTreeNode) } : {}),
  };
};

const nodes = computed(() => props.items.map(toTreeNode));

// Every branch starts open, as it did with `default-expand-level="Infinity"`;
// from there the toggle arrow owns the state, and a branch the user collapsed
// stays collapsed while new items keep arriving
const expandedKeys = ref<Record<string, boolean>>({});
watch(
  () => props.items,
  (items) => {
    expandedKeys.value = Object.fromEntries(
      flatten(items).map((node) => [
        keyOf(node),
        expandedKeys.value[keyOf(node)] ?? true,
      ]),
    );
  },
  { immediate: true },
);

/** Rebuilds PrimeVue's tri-state map from the ids the model carries. */
const selectionKeys = computed(() => {
  const keys: TreeSelectionKeys = {};

  const visit = (source: TreeviewSource): NodeState => {
    const children = childrenOf(source);

    if (!children.length) {
      const checked = selectedLeaves.value.has(source.id);
      if (checked) keys[keyOf(source)] = { checked: true };
      return checked ? 'checked' : 'none';
    }

    const states = children.map(visit);
    let state: NodeState = 'none';
    if (states.every((child) => child === 'checked')) state = 'checked';
    else if (states.some((child) => child !== 'none')) state = 'partial';

    if (state === 'checked') keys[keyOf(source)] = { checked: true };
    else if (state === 'partial')
      keys[keyOf(source)] = { partialChecked: true };

    return state;
  };

  props.items.forEach(visit);
  return keys;
});

/** Folds a fully selected branch back into the branch id itself. */
const handleSelection = (keys: TreeSelectionKeys) => {
  const picked: TreeviewId[] = [];

  const visit = (source: TreeviewSource) => {
    const children = childrenOf(source);

    if (keys[keyOf(source)]?.checked && isSelectable(source)) {
      picked.push(source.id);
      return;
    }
    children.forEach(visit);
  };

  props.items.forEach(visit);
  emit('update:modelValue', [...picked, ...unknownIds.value]);
};
</script>

<style lang="scss">
.cn-treeview-wrapper {
  > :not([hidden]) {
    ~ :not([hidden]) {
      margin-top: var(--size-xl);
    }
  }
  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

// PrimeVue 4 is unstyled — the class names come from the pass-through map in
// `config/primevue.js`, everything they hang on lives here
.cn-treeview {
  --ab-canyon-treeview-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-treeview-option-color-bg-active: var(
    --ab-canyon-color-neutral-light
  );
  --ab-canyon-treeview-checkbox-color-border: var(--ab-canyon-color-neutral);
  --ab-canyon-treeview-checkbox-color-border-checked: var(
    --ab-canyon-color-accent-4
  );
  --ab-canyon-treeview-checkbox-color-bg-checked: var(
    --ab-canyon-color-accent-4
  );
  --ab-canyon-treeview-checkbox-color-border-hover: var(
    --ab-canyon-color-accent-4
  );
  --ab-canyon-treeview-checkbox-color-bg-hover: var(--ab-canyon-color-light);
  --ab-canyon-treeview-checkbox-color-mark: var(--ab-canyon-color-light);
  --ab-canyon-treeview-toggle-color: var(--ab-canyon-color-neutral);

  .p-tree-root-children,
  .p-tree-node-children {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  // vue-treeselect indented every level by 20px
  .p-tree-node-children {
    padding-left: 20px;
  }

  .p-tree-node-content {
    align-items: center;
    display: flex;
    padding-left: 5px;
    padding-top: 10px;
    padding-bottom: 10px;

    &[data-p-selectable='true']:hover {
      background-color: var(--ab-canyon-treeview-option-color-bg-active);

      .p-checkbox:not([data-p-checked='true']):not(
          [data-p-indeterminate='true']
        )
        .p-checkbox-box {
        border-color: var(--ab-canyon-treeview-checkbox-color-border-hover);
        background: var(--ab-canyon-treeview-checkbox-color-bg-hover);
      }
    }
  }

  // PrimeVue renders the toggle button on every node, leaves included, which is
  // what keeps the arrow column aligned across a branch and its siblings.
  // vue-treeselect reserved that column only once the tree held a branch, so a
  // flat tree drops it here too and its rows start at the container edge.
  .p-tree-node-toggle-button {
    justify-content: center;
    align-items: center;
    display: flex;
    height: 22px;
    width: 20px;
  }

  // Whether the tree has branches at all is read from `data-p-leaf`, not from
  // the rendered child lists: collapsing every branch removes those, and the
  // column would take the arrows with it, leaving nothing to expand back
  &:not(:has(.p-tree-node-toggle-button[data-p-leaf='false']))
    .p-tree-node-toggle-button {
    display: none;
  }

  &__toggle-icon {
    color: var(--ab-canyon-treeview-toggle-color);
    height: 9px;
    width: 9px;
  }

  // PrimeVue draws the toggle icon on leaves as well; there it is only a
  // spacer, exactly like vue-treeselect's arrow placeholder
  .p-tree-node-toggle-button[data-p-leaf='true'] {
    pointer-events: none;

    .cn-treeview__toggle-icon {
      visibility: hidden;
    }
  }

  .p-checkbox {
    line-height: 0;
    position: relative;
    margin: 0 2px;

    input {
      opacity: 0;
      position: absolute;
      inset: 0;
      margin: 0;
      cursor: pointer;
    }
  }

  .p-checkbox-box {
    border: 1px solid var(--ab-canyon-treeview-checkbox-color-border);
    width: var(--size-base);
    height: var(--size-base);
    align-items: center;
    justify-content: center;
    display: flex;
  }

  .p-checkbox-icon {
    color: var(--ab-canyon-treeview-checkbox-color-mark);
    width: var(--size-xs);
    height: var(--size-xs);
  }

  .p-checkbox[data-p-checked='true'],
  .p-checkbox[data-p-indeterminate='true'] {
    .p-checkbox-box {
      border-color: var(--ab-canyon-treeview-checkbox-color-border-checked);
      background: var(--ab-canyon-treeview-checkbox-color-bg-checked);
    }
  }

  .p-tree-node-label {
    color: var(--ab-canyon-treeview-color-text);
    padding-left: calc(var(--size-base) / 2);
    white-space: normal;
    line-height: 22px;
  }
}
</style>
